// frontend/src/features/campaign/components/create/ImportPipelineLeadsModal.js
import React from 'react';
import { getStageClass, getScoreClass } from '../../utils/campaignUtils';

export default function ImportPipelineLeadsModal({
  isOpen,
  onClose,
  modalSearch,
  setModalSearch,
  modalStageFilter,
  setModalStageFilter,
  modalScoreFilter,
  setModalScoreFilter,
  onFetchPipelineLeads,
  isLoadingPipelineLeads,
  filteredModalLeads,
  selectedLeadIds,
  onToggleSelectAll,
  onToggleSelectLead,
  formData,
  onConfirmImport,
}) {
  if (!isOpen) return null;

  return (
    <div className="cmp-modal-backdrop" onClick={onClose}>
      <div className="cmp-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="cmp-modal-header">
          <div className="cmp-modal-header-left">
            <div className="cmp-modal-icon-badge">
              <i className="fas fa-database" />
            </div>
            <div>
              <h3 className="cmp-modal-title">Import Leads from CRM Lead Pipeline</h3>
              <p className="cmp-modal-subtitle">
                Select verified decision-makers from your workspace pipeline to attach to this
                campaign strategy cohort.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="cmp-modal-close-btn"
            onClick={onClose}
            title="Close modal"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Modal Filter Bar */}
        <div className="cmp-modal-filter-bar">
          <div className="cmp-modal-search-box">
            <i className="fas fa-search" />
            <input
              type="text"
              placeholder="Search by contact name, company, title, industry, or location..."
              value={modalSearch}
              onChange={(e) => setModalSearch(e.target.value)}
            />
            {modalSearch && (
              <button type="button" className="cmp-search-clear" onClick={() => setModalSearch('')}>
                <i className="fas fa-times" />
              </button>
            )}
          </div>

          <div className="cmp-modal-filters-row">
            <div className="cmp-filter-group">
              <label>Stage:</label>
              <select
                value={modalStageFilter}
                onChange={(e) => setModalStageFilter(e.target.value)}
              >
                <option value="ALL">All Stages</option>
                <option value="New">New</option>
                <option value="Contacted">Contacted</option>
                <option value="Engaged">Engaged</option>
                <option value="Qualified">Qualified</option>
                <option value="Proposal">Proposal</option>
                <option value="Won">Won</option>
              </select>
            </div>

            <div className="cmp-filter-group">
              <label>ICP Fit:</label>
              <select
                value={modalScoreFilter}
                onChange={(e) => setModalScoreFilter(e.target.value)}
              >
                <option value="ALL">All Fit Scores</option>
                <option value="HIGH">High Fit (80-100)</option>
                <option value="MED">Medium Fit (60-79)</option>
              </select>
            </div>

            <button
              type="button"
              className="cmp-btn-secondary cmp-refresh-pipeline-btn"
              onClick={onFetchPipelineLeads}
              disabled={isLoadingPipelineLeads}
              title="Refresh from database"
            >
              <i className={`fas fa-sync-alt ${isLoadingPipelineLeads ? 'fa-spin' : ''}`} />{' '}
              {isLoadingPipelineLeads ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Selection info bar */}
        <div className="cmp-modal-info-bar">
          <span>
            Showing <strong>{filteredModalLeads.length}</strong> available pipeline leads
          </span>
          <div className="cmp-modal-info-right">
            <span>
              <strong>{selectedLeadIds.size}</strong> selected for import
            </span>
            <button
              type="button"
              className="cmp-link-btn"
              onClick={() => onToggleSelectAll(filteredModalLeads)}
            >
              {filteredModalLeads.filter((l) => !l.inOtherCampaign).length > 0 &&
              filteredModalLeads
                .filter((l) => !l.inOtherCampaign)
                .every((l) => selectedLeadIds.has(l.id))
                ? 'Deselect All'
                : 'Select All Selectable'}
            </button>
          </div>
        </div>

        {/* Modal Body / Table */}
        <div className="cmp-modal-table-wrap">
          {isLoadingPipelineLeads ? (
            <div className="cmp-modal-loading-state">
              <i className="fas fa-spinner fa-spin" />
              <p>Retrieving pipeline leads from database...</p>
            </div>
          ) : filteredModalLeads.length === 0 ? (
            <div className="cmp-modal-empty-state">
              <i className="fas fa-filter" />
              <h4>No Matching Pipeline Leads Found</h4>
              <p>Try adjusting your search criteria or stage filter.</p>
            </div>
          ) : (
            <table className="cmp-modal-leads-table">
              <thead>
                <tr>
                  <th style={{ width: '44px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={
                        filteredModalLeads.filter((l) => !l.inOtherCampaign).length > 0 &&
                        filteredModalLeads
                          .filter((l) => !l.inOtherCampaign)
                          .every((l) => selectedLeadIds.has(l.id))
                      }
                      onChange={() => onToggleSelectAll(filteredModalLeads)}
                    />
                  </th>
                  <th>Lead Contact</th>
                  <th>Title & Role</th>
                  <th>Company & Sector</th>
                  <th>Location</th>
                  <th>Stage</th>
                  <th>ICP Score</th>
                  <th>Channels</th>
                </tr>
              </thead>
              <tbody>
                {filteredModalLeads.map((lead) => {
                  const isSelected = selectedLeadIds.has(lead.id);
                  const isAlreadyImported = (formData.importedLeads || []).some(
                    (l) => l.id === lead.id
                  );
                  const isInOtherCampaign = Boolean(lead.inOtherCampaign);

                  return (
                    <tr
                      key={lead.id}
                      className={`${isSelected ? 'selected-row' : ''} ${isAlreadyImported ? 'already-imported-row' : ''} ${isInOtherCampaign ? 'cmp-lead-row-disabled' : ''}`}
                      onClick={() => !isInOtherCampaign && onToggleSelectLead(lead.id)}
                      style={isInOtherCampaign ? { opacity: 0.65, cursor: 'not-allowed' } : {}}
                      title={
                        isInOtherCampaign
                          ? `Enrolled in active campaign "${lead.otherCampaignName}" (${lead.otherCampaignStatus}). Cannot be imported until that campaign ends or is deleted.`
                          : undefined
                      }
                    >
                      <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isInOtherCampaign}
                          onChange={() => !isInOtherCampaign && onToggleSelectLead(lead.id)}
                        />
                      </td>
                      <td>
                        <div className="cmp-lead-contact-cell">
                          <div className="cmp-lead-avatar">
                            {lead.name
                              ? lead.name
                                  .split(' ')
                                  .map((n) => n[0])
                                  .slice(0, 2)
                                  .join('')
                                  .toUpperCase()
                              : 'L'}
                          </div>
                          <div>
                            <div className="cmp-lead-name-row">
                              <strong className="cmp-lead-name">{lead.name}</strong>
                              {isInOtherCampaign ? (
                                <span
                                  className="cmp-already-tag cmp-tag-other-campaign"
                                  title={`Active in campaign: ${lead.otherCampaignName || 'Other Campaign'} (${lead.otherCampaignStatus || 'Active'})`}
                                >
                                  <i className="fas fa-bullhorn" style={{ marginRight: '4px' }} />
                                  In Other Campaign
                                </span>
                              ) : isAlreadyImported ? (
                                <span className="cmp-already-tag">In Cohort</span>
                              ) : null}
                            </div>
                            <span className="cmp-lead-email">{lead.email || 'No email'}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="cmp-lead-title">{lead.jobTitle}</span>
                      </td>
                      <td>
                        <strong className="cmp-lead-company">{lead.company}</strong>
                        <span className="cmp-lead-industry-tag">{lead.industry}</span>
                      </td>
                      <td>
                        <span className="cmp-lead-country">
                          <i className="fas fa-map-marker-alt" /> {lead.country}
                        </span>
                      </td>
                      <td>
                        <span className={`cmp-stage-badge ${getStageClass(lead.stage)}`}>
                          {lead.stage}
                        </span>
                      </td>
                      <td>
                        <span className={`cmp-score-badge ${getScoreClass(lead.leadScore)}`}>
                          {lead.leadScore} Fit
                        </span>
                      </td>
                      <td>
                        <div className="cmp-lead-channels-icons">
                          {lead.email && (
                            <i className="fas fa-envelope active" title={lead.email} />
                          )}
                          {lead.linkedinUrl && (
                            <i className="fab fa-linkedin active" title="LinkedIn Profile" />
                          )}
                          {lead.phone && <i className="fas fa-phone active" title={lead.phone} />}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Modal Footer */}
        <div className="cmp-modal-footer">
          <div className="cmp-modal-footer-left">
            <span>
              <strong>{selectedLeadIds.size}</strong> leads selected to import
            </span>
          </div>
          <div className="cmp-modal-footer-right">
            <button type="button" className="cmp-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="cmp-btn-primary"
              disabled={selectedLeadIds.size === 0}
              onClick={onConfirmImport}
            >
              <i className="fas fa-file-import" /> Import{' '}
              {selectedLeadIds.size > 0 ? `${selectedLeadIds.size} ` : ''}Leads into Cohort
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
